import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/feature/Seo";
import { brand } from "@/mocks/siteData";

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  points?: string[];
}

interface LegalPageLayoutProps {
  eyebrow: string;
  title: string;
  intro: string;
  updated: string;
  path: string;
  seoTitle: string;
  seoDescription: string;
  seoImage?: string;
  sections: LegalSection[];
}

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function LegalPageLayout({
  eyebrow,
  title,
  intro,
  updated,
  path,
  seoTitle,
  seoDescription,
  seoImage,
  sections,
}: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo title={seoTitle} description={seoDescription} path={path} image={seoImage} />
      <Navbar />

      <main>
        <section className="sun-glaze bg-background-100 pt-32 pb-14 md:pt-40 md:pb-16 border-b border-background-200/70">
          <div className="w-full px-4 md:px-8">
            <Reveal className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
                <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                  {eyebrow}
                </span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl leading-tight text-foreground-950 font-semibold">
                {title}
              </h1>
              <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">{intro}</p>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-background-50 border border-background-200/70 px-4 py-2 text-xs font-medium text-foreground-700">
                <span className="w-4 h-4 flex items-center justify-center text-accent-600">
                  <i className="ri-calendar-check-line"></i>
                </span>
                Last updated: {updated}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <Reveal className="lg:col-span-4 lg:sticky lg:top-28">
                <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6">
                  <h2 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">
                    On this page
                  </h2>
                  <ul className="space-y-3">
                    {sections.map((section, index) => (
                      <li key={section.heading}>
                        <a
                          href={`#${slugify(section.heading)}`}
                          className="group flex items-start gap-3 text-sm text-foreground-600 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <span className="text-xs font-semibold text-secondary-500 mt-0.5">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <span className="leading-snug">{section.heading}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-6 border-t border-secondary-200">
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      Questions about this document? We're happy to help.
                    </p>
                    <Link
                      to="/contact"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Contact {brand.name}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </Reveal>

              <Reveal className="lg:col-span-8" delay={100}>
                <article className="space-y-10">
                  {sections.map((section, index) => (
                    <section
                      key={section.heading}
                      id={slugify(section.heading)}
                      className="scroll-mt-28"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
                          {section.heading}
                        </h2>
                      </div>
                      {section.paragraphs.map((paragraph) => (
                        <p
                          key={paragraph}
                          className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3"
                        >
                          {paragraph}
                        </p>
                      ))}
                      {section.points && section.points.length > 0 ? (
                        <ul className="mt-4 space-y-2.5">
                          {section.points.map((point) => (
                            <li key={point} className="flex items-start gap-3">
                              <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-600">
                                <i className="ri-checkbox-circle-line"></i>
                              </span>
                              <span className="text-sm md:text-base text-foreground-700 leading-relaxed">
                                {point}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </section>
                  ))}

                  <div className="rounded-lg bg-primary-900 text-background-100 p-6 md:p-8">
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-background-50">
                      Still have questions?
                    </h2>
                    <p className="mt-3 text-sm text-background-200 leading-relaxed max-w-xl">
                      Reach the {brand.fullName} team and we'll walk you through anything you're
                      unsure about.
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-4">
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Contact us
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                      <a
                        href={`mailto:${brand.email}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-background-100 hover:text-accent-300 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-mail-line"></i>
                        {brand.email}
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}